/**
 * Utility functions for referral system
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Get or generate referral code for current user
 */
export async function getUserReferralCode(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc('get_user_referral_code', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error getting referral code:', error);
      return null;
    }

    return data as string;
  } catch (error) {
    console.error('Error in getUserReferralCode:', error);
    return null;
  }
}

/**
 * Get referral link for current user
 */
export async function getUserReferralLink(userId: string): Promise<string | null> {
  const code = await getUserReferralCode(userId);
  if (!code) return null;

  const baseUrl = window.location.origin;
  return `${baseUrl}/auth?ref=${code}`;
}

/**
 * Track referral when new user signs up
 */
export async function trackReferral(referralCode: string, newUserId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('track_referral', {
      p_referral_code: referralCode,
      p_new_user_id: newUserId
    });

    if (error) {
      console.error('Error tracking referral:', error);
      return false;
    }

    return data as boolean;
  } catch (error) {
    console.error('Error in trackReferral:', error);
    return false;
  }
}

/**
 * Get count of successful referrals for user
 */
export async function getUserReferralCount(userId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('referrals')
      .select('*', { count: 'exact', head: true })
      .eq('referrer_id', userId);

    if (error) {
      console.error('Error getting referral count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error in getUserReferralCount:', error);
    return 0;
  }
}

/**
 * Get referral stats for user
 */
export async function getUserReferralStats(userId: string) {
  try {
    const [referralCount, referralCode] = await Promise.all([
      getUserReferralCount(userId),
      getUserReferralCode(userId)
    ]);

    return {
      totalReferrals: referralCount,
      referralCode,
      referralLink: referralCode ? `${window.location.origin}/auth?ref=${referralCode}` : null
    };
  } catch (error) {
    console.error('Error in getUserReferralStats:', error);
    return {
      totalReferrals: 0,
      referralCode: null,
      referralLink: null
    };
  }
}

/**
 * Store referral code in localStorage temporarily (before signup)
 */
export function storeReferralCode(code: string): void {
  try {
    localStorage.setItem('dino_referral_code', code);
  } catch (error) {
    console.error('Error storing referral code:', error);
  }
}

/**
 * Get stored referral code from localStorage
 */
export function getStoredReferralCode(): string | null {
  try {
    return localStorage.getItem('dino_referral_code');
  } catch (error) {
    console.error('Error getting stored referral code:', error);
    return null;
  }
}

/**
 * Clear stored referral code from localStorage
 */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem('dino_referral_code');
  } catch (error) {
    console.error('Error clearing stored referral code:', error);
  }
}
